import React, { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Box, Button, Typography, Stepper, Step, StepLabel } from '@mui/material';
import { useMutation } from '@apollo/client/react';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { DEFAULT_COMPANY_INFO, MAX_FILE_SIZE_IN_MEGA_BYTES } from '@/utils/constants';
import useDirectUpload from '@/hooks/useDirectUpload';
import { displayNotification } from '@/utils/notifications';
import { isValidEmail } from '@/utils/userInformation';
import { SignInContainer, WiderStyledCard } from '../Auth/StyledComponents';
import CompanyLogoDropzone from './CompanyLogoDropzone';
import CompanyDetailsFields from './CompanyDetailsFields';
import CircularLoader from '@/components/Loaders/CircularLoader';
import {
  CreateCompanyDocument,
  UpdateCompanyDocument,
  SignOutDocument,
  CompaniesHouseInfo,
} from '@/graphql/generated/graphql';
import ControlledModals from '@/components/ShortcutSpider/ControlledModals';
import PlanSelectionStep from './PlanSelectionStep';
import { isUndefined } from 'lodash';
import { titleize } from 'inflection';

const OnboardingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { currentUser, refetchCurrentUser } = useContext(CurrentUserContext);
  const [activeStep, setActiveStep] = useState(0);
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [finishedOnboarding, setFinishedOnboarding] = useState(false);
  const [pendingLogoSignedId, setPendingLogoSignedId] = useState<string | null>(null);
  const [pendingLogoPreviewUrl, setPendingLogoPreviewUrl] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const { isUploading: isLogoUploading, uploadFile: uploadLogoFile } = useDirectUpload();

  const defaultCompany = currentUser?.defaultCompany;
  const isUpdating = Boolean(defaultCompany?.id);
  const steps = ['Company details',  'Choose a plan'];

  const [createCompany, { loading: creatingCompany }] = useMutation(CreateCompanyDocument);
  const [updateCompany, { loading: updatingCompany }] = useMutation(UpdateCompanyDocument);
  const [signOut] = useMutation(SignOutDocument, {
    onCompleted: (data) => {
      if (data.signOut) {
        // eslint-disable-next-line
        location.href = location.href;
      }
    },
  });

  useEffect(() => {
    if (searchParams.get('onboarding_step') === 'plan') {
      setActiveStep(1);
    }
  }, [searchParams]);

  const clearPendingCompanyLogo = () => {
    if (pendingLogoPreviewUrl) {
      URL.revokeObjectURL(pendingLogoPreviewUrl);
    }
    setPendingLogoSignedId(null);
    setPendingLogoPreviewUrl(null);
  };

  const handleCompanyLogoFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      displayNotification({ type: 'error', text: 'Please choose an image file (PNG, JPG, WebP, etc.).' });
      return;
    }
    if (file.size / 1000000 > MAX_FILE_SIZE_IN_MEGA_BYTES) {
      displayNotification({
        type: 'error',
        text: `Max allowed file size is ${MAX_FILE_SIZE_IN_MEGA_BYTES} MB.`,
      });
      return;
    }

    setPendingLogoPreviewUrl((previousPreviewUrl) => {
      if (previousPreviewUrl) URL.revokeObjectURL(previousPreviewUrl);
      return URL.createObjectURL(file);
    });
    setPendingLogoSignedId(null);

    const result = await uploadLogoFile(file, 'company-logos');
    if (result?.signedBlobId) {
      setPendingLogoSignedId(result.signedBlobId);
    } else {
      displayNotification({ type: 'error', text: 'Logo upload failed. Please try again.' });
      setPendingLogoPreviewUrl((previousPreviewUrl) => {
        if (previousPreviewUrl) URL.revokeObjectURL(previousPreviewUrl);
        return null;
      });
    }
  };

  const handleSaveCompany = () => {
    const sharedVariables = {
      name: companyInfo.name as string,
      primaryEmail: companyInfo.email,
      primaryPhone: companyInfo.phone,
      street: companyInfo.street,
      city: selectedCity as string,
      state: selectedState as string,
      country: 'United Kingdom',
      zip: companyInfo.zip,
      companyNumber: companyInfo.companyNumber,
      tradingName: companyInfo.tradingName || undefined,
      vatNumber: companyInfo.vatNumber || undefined,
      companyType: companyInfo.companyType,
      ...(pendingLogoSignedId ? { logoSignedId: pendingLogoSignedId } : {}),
    };
    const onCompanyComplete = () => {
      clearPendingCompanyLogo();
      setActiveStep(1);
    };

    if (isUpdating) {
      updateCompany({
        variables: { id: defaultCompany!.id, ...sharedVariables },
        onCompleted: onCompanyComplete,
        onError: () => displayNotification({ type: 'error', text: 'Unable to update company. Please try again.' }),
      });
    } else {
      createCompany({
        variables: {
          ownerId: currentUser?.id as string,
          isParent: true,
          ...sharedVariables,
        },
        onCompleted: onCompanyComplete,
        onError: () => displayNotification({ type: 'error', text: 'Unable to create company. Please try again.' }),
      });
    }
  };

  const handleFinishOnboarding = () => {
    setFinishedOnboarding(true);
    refetchCurrentUser?.();
  };

  const handleCompanyInfoChange = (key: string, value: string) => {
    setCompanyInfo((prev) => ({ ...prev, [key]: value }));
  };

  const handleAutofill = (companiesHouseInfo: CompaniesHouseInfo) => {
    if (isUndefined(companiesHouseInfo) || !companiesHouseInfo?.success || !companiesHouseInfo?.data) return;

    const { name, companyType, address } = companiesHouseInfo.data;

    setCompanyInfo((prev) => ({ ...prev,
      name: titleize(name),
      street: address?.street ?? prev.street,
      city: address?.city ?? "",
      state: address?.state ?? "",
      zip: address?.zip ?? prev.zip,
      companyType: companyType ?? prev.companyType,
    }));
  }

  const isCompanyFormValid =
    companyInfo.name.trim() !== '' &&
    companyInfo.email.trim() !== '' &&
    isValidEmail(companyInfo.email) &&
    companyInfo.phone.trim() !== '' &&
    companyInfo.street?.trim() !== '' &&
    selectedState != null &&
    selectedCity != null &&
    companyInfo.zip?.trim() !== '' &&
    companyInfo.companyNumber.trim() !== '';

  useEffect(() => {
    if (!defaultCompany) return;
    setCompanyInfo({
      id: defaultCompany.id,
      name: defaultCompany.name,
      email: defaultCompany.primaryEmail,
      phone: defaultCompany.primaryPhone as string,
      street: defaultCompany.address?.street as string,
      zip: defaultCompany.address?.zip as string,
      companyNumber: defaultCompany.companyNumber,
      tradingName: defaultCompany.tradingName as string,
      vatNumber: defaultCompany.vatNumber as string,
      companyType: defaultCompany.companyType,
    });

    if (defaultCompany.address?.state) setSelectedState(defaultCompany.address.state);
    if (defaultCompany.address?.city) setSelectedCity(defaultCompany.address.city);
    // Only run when defaultCompany identity changes (id), not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCompany?.id]);

  if (!currentUser || finishedOnboarding) return <CircularLoader />;

  const companyLogoUrl = pendingLogoPreviewUrl ?? defaultCompany?.imageUrl ?? null;

  return (
    <SignInContainer direction="column" justifyContent="space-between" sx={{ marginTop: 5 }}>
      <Button variant="outlined" size="small" onClick={() => signOut()} sx={{ position: 'fixed', top: 16, right: 16 }}>
        Logout
      </Button>
      <WiderStyledCard variant="outlined" sx={{ maxWidth: 600 }}>
        <Typography variant="h4" textAlign="center" fontWeight={600} sx={{ mb: 0 }}>
          Welcome to TheOverwatch.AI
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          Set up your workspace to get started.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {(activeStep === 0) && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveCompany();
            }}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
          >
            <CompanyLogoDropzone
              companyLogoUrl={companyLogoUrl}
              isLogoUploading={isLogoUploading}
              pendingLogoSignedId={pendingLogoSignedId}
              fileInputRef={logoFileInputRef}
              onFile={handleCompanyLogoFile}
              onClear={clearPendingCompanyLogo}
            />
            <CompanyDetailsFields
              companyInfo={companyInfo}
              onFieldChange={handleCompanyInfoChange}
              selectedState={selectedState}
              onSelectedStateChange={setSelectedState}
              selectedCity={selectedCity}
              onSelectedCityChange={setSelectedCity}
              handleAutofill={handleAutofill}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              disabled={!isCompanyFormValid || creatingCompany || updatingCompany || isLogoUploading}
            >
              {creatingCompany || updatingCompany ? (isUpdating ? 'Saving...' : 'Creating...') : 'Continue'}
            </Button>
          </Box>
        )}

        {activeStep === 1 && (
          <PlanSelectionStep onComplete={handleFinishOnboarding} />
        )}
      </WiderStyledCard>

      <ControlledModals />
    </SignInContainer>
  );
};

export default OnboardingPage;
